import { CdkMenuTrigger } from '@angular/cdk/menu';
import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { Paginator } from 'src/app/models/paginator';
import { Tag } from 'src/app/models/tag';
import { User } from 'src/app/models/user';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { MessageService } from 'src/app/services/message.service';
import { TagService } from 'src/app/services/tag.service';

type TagOption = Tag & { selected: boolean, disabled?: boolean, display?: boolean }

@Component({
  selector: 'app-tag-select',
  templateUrl: './tag-select.component.html',
  styleUrl: './tag-select.component.scss'
})
export class TagSelectComponent {
  private id: string;
  @Input() label?: string;
  @Input() labelOutside: boolean = false;
  @Input() search: boolean = false;
  @Input() multiple: boolean = false;

  @Input() initial?: string = 'all';
  @Input() disabled: boolean = false;
  @Input() disabledValues: Tag[] = [];
  @Input() emitOnInit: boolean = true;

  loading: boolean = false;
  tags: Paginator<TagOption> = {
    docs: [],
    page: 1,
    offset: 0,
    limit: 10,
    totalDocs: 0
  };

  selected: TagOption[] = [];

  query?: string;
  queryUpdate: Subject<string | undefined> = new Subject<string | undefined>();

  @Output() change = new EventEmitter<any>();
  @Input() value?: Tag | Tag[];
  @Output() valueChange = new EventEmitter<any>();

  show: boolean = false;

  selection?: string;

  subscription: Subscription;

  @ViewChild('trigger') trigger?: CdkMenuTrigger;

  user?: User;

  constructor(
    private _message: MessageService,
    private authService: AuthenticationService,
    private tagService: TagService,
    private translate: TranslateService
  ) {
    this.id = _.uniqueId("SELECT-");
    this.subscription = this._message.changeEmitted$.subscribe(change => {
      if (change && change.key == "LAYOUT.SELECT") {
        (change.data.id !== this.id) && (this.show = false);
      }
    });
    this.queryUpdate
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(async () => {
        this.resetPage();
        await this.retrieveProductsAndServices(true);
      });
    this.authService.user.subscribe((user: User | undefined) => {
      this.user = user;
    });
  }

  ngOnInit(): void {
    this.setInitialState();
  }


  _isDisabled(option: Tag) {
    return (option._id && this.disabledValues.map(v => v._id).includes(option._id)) ? true : false;
  }

  _isSelected(option: Tag) {
    return (option._id && this.selected.map((t) => t._id).includes(option._id)) ? true : false;
  }

  populate() {
    if (this.multiple && this.value && (this.value as Tag[]).length > 0) {
      const values = (this.value as Tag[]);
      this.selected = values.map((t) => ({ ...t, selected: true, disabled: false, display: true }));
    } else if (!this.multiple && this.value) {
      const value = (this.value as Tag);
      this.selected = [{ ...value, selected: true, disabled: false, display: true }];
    }
  }

  selectedToDisplay() {
    return this.selected.filter((o) => o.display);
  }

  async setInitialState() {
    await this.retrieveProductsAndServices(true);
    if (this.multiple && this.value && (this.value as Tag[]).length > 0) {
      const values = (this.value as Tag[]);
      this.selected = values.map((t) => ({ ...t, selected: true, disabled: false }));
      this.tags.docs.forEach(tag => {
        const found = values.find(t => t._id === tag._id);
        if (found) {
          tag.selected = true;
        } else {
          tag.selected = false;
        }
      });
      const value = _.unionWith([...this.tags.docs.filter(tag => tag.selected), ...this.selected], this._tagComparator);
      if (value.length > 0) {
        this.selection = value.length === 1 ? (this.truncate(value[0].label, 25)) : `${this.truncate(this.label)} (${value.length})`;
      } else {
        this.selection = undefined;
      }
    } else if (!this.multiple && this.value) {
      const value = (this.value as Tag);
      this.selected = [{ ...value, selected: true, disabled: false }];
      const option = this.tags.docs.find(tag => tag._id === value._id);
      if (option) {
        option.selected = true;
      }
      this.selection = value ? (this.truncate(value.label)) : undefined;
    }
    if (this.emitOnInit) {
      this.onChange();
    }

  }

  removeFromPreselection(option: any) {
    this.selected.splice(this.selected.findIndex((o) => option._id === o._id), 1);
    const doc = this.tags.docs.find((tag) => tag._id === option._id);
    if (doc) {
      doc.selected = false;
    }
    this.onChange()
  }

  toggleFromSelection(option: TagOption) {
    if (!option.selected) {
      this.selected.splice(this.selected.findIndex((o) => option._id === o._id), 1);
    } else if (!this._isSelected(option)) {
      this.selected.push({ ...option, display: false });
    }
    this.selected.sort((a, b) => (a.label || 'a').localeCompare(b.label || 'a'))
  }

  resetPage() {
    this.tags.limit = 10;
    this.tags.page = 1;
    this.tags.offset = 0;
  }

  _tagComparator(productServiceA: Tag, productServiceB: Tag) {
    return productServiceA._id === productServiceB._id;
  }

  async retrieveProductsAndServices(replace: boolean = false) {
    this.loading = true;
    const result = await this.tagService.retrieveAll(this.tags.page, this.tags.limit, this.tags.offset, this.query)
    this.tags = {
      ...result,
      docs: replace ? [...result.docs.map(tag => ({ ...tag, disabled: this._isDisabled(tag), selected: this._isSelected(tag) }))] : _.unionWith([...this.tags.docs, ...result.docs.map(tag => ({ ...tag, disabled: this._isDisabled(tag), selected: this._isSelected(tag) }))], this._tagComparator)
    };
    this.loading = false;
  }

  select(option: any) {
    option.selected = option.selected !== null ? !option.selected : true;
    this.toggleFromSelection(option)
    if (!this.multiple) {
      this.tags.docs.forEach(t => {
        if (t._id !== option._id) {
          t.selected = false;
        }
      })
    }
    this.onChange();
  }

  // TODO: handle trademarks having countrycode which is a wipo code (ex: EM)
  onChange() {
    if (!this.multiple) {
      const value = this.tags.docs.find(c => c.selected) !== undefined ? this.tags.docs.find(c => c.selected) : (this.selected.length === 1 ? this.selected[0] : undefined);
      this.selection = value ? `${this.truncate(this.label)} (1)` : undefined;
      this.valueChange.emit(value);
      this.change.emit({ value });
      this.trigger?.close();
    } else {
      const values = _.unionWith([...this.tags.docs.filter(c => c.selected), ...this.selected], this._tagComparator);
      if (values.length > 0) {
        this.selection = `${this.truncate(this.label)} (${values.length})`;
      } else {
        this.selection = undefined;
      }
      this.valueChange.emit(values);
      this.change.emit({ value: values });
    }
  }


  toggle() {
    this.show = !this.show;
    if (this.show) {
      this._message.emitChange("LAYOUT", "SELECT", {
        id: this.id
      });
    }
  }

  filtered() {
    return this.tags.docs;
  }

  onClear() {
    if (this.multiple) {
      this.value = [];
    } else {
      this.value = undefined;
    }
    this.tags.docs.forEach(tag => {
      tag.selected = false;
    });
    this.selected = [];
    this.selection = undefined;
    this.query = undefined;
    this.onChange();
    this.trigger?.close();
  }

  onQueryChange(event: any) {
    this.queryUpdate.next(this.query);
  }


  onScroll() {

    this.tags.offset = this.tags.page * this.tags.limit;
    this.tags.page = this.tags.page + 1;
    this.retrieveProductsAndServices();
  }

  truncate(value?: string, number?: number) {
    if (!value || value.length < (number || 20)) return value;
    return `${value.substring(0, (number || 20))}...`
  }

}
